<script lang="ts">
// Module-scoped stack of currently-open MidFlowModal panels, shared across ALL
// instances (a plain <script> block runs once at module load, unlike <script
// setup> which runs per-instance). Only the TOPMOST open modal runs its focus
// trap; every lower modal yields.
//
// Without this, two stacked modals (e.g. the ACH authorization overlay opening
// over the Pay Modal — both MidFlowModals, both Teleported to <body>, so neither
// is a DOM descendant of the other) each register a document `focusin` trap.
// Each trap reads focus sitting in the OTHER modal's panel as "escaped" and
// synchronously calls .focus() to pull it back, which re-dispatches `focusin`
// and re-enters both handlers — recursing until the call stack overflows
// (RangeError: Maximum call stack size exceeded). Gating the trap to the topmost
// modal breaks the ping-pong.
const openModalPanels: HTMLElement[] = [];
</script>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch, nextTick } from 'vue';

defineOptions({ inheritAttrs: false });

interface Props {
  visible: boolean;
  title: string;
  /**
   * Desktop modal width preset.
   * - `md` (default, 480px) fits most short forms (cancel confirm, share).
   *   Phase 5.3 Open Item 2: the pay-modal sketch renders ~520px, but we
   *   ACCEPT the existing `md` (480px) token rather than introduce a fourth
   *   width — the 480px card holds the card/ACH form comfortably and avoids a
   *   one-off preset that no other consumer needs. (Documented choice.)
   * - `lg` (720px) gives dual-pane bodies a comfortable middle ground.
   * - `xl` (1200px) matches the sketch's `--max-content` page width so the
   *   modal reads as a peer of the page; used by RescheduleDialog
   *   (PODZ-1699). On tablets `w-full + sm:mx-4` naturally shrinks the panel
   *   to the viewport — so an `xl` modal renders ~1200px on desktop and
   *   ~viewport-32px on tablet. Phone behavior is unchanged: panel is always
   *   full viewport via `max-sm:w-screen max-sm:h-dvh`.
   */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /**
   * When `true`, the header bar (title + close button + bottom border) is not
   * rendered — for compact confirm dialogs that carry their own title in the
   * body (sketch 6.2 `.pc-card`). `title` is still used for the panel's
   * aria-label. Defaults `false` so every form modal keeps its header.
   */
  hideHeader?: boolean;
  /**
   * Phase 5.3 (D-09 / AC#13): when `false` the modal CANNOT be dismissed —
   * Escape, backdrop click, and the header close button are all gated off.
   * The pay modal sets `closable=false` while a transaction is in flight so
   * the customer can't navigate away mid-charge. Defaults `true` so every
   * existing consumer (Cancel / Reschedule / Share / wizard modals) keeps the
   * dismissible behavior they rely on.
   */
  closable?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  closable: true,
  hideHeader: false,
});
const emit = defineEmits<{ 'update:visible': [value: boolean] }>();

const panelRef = ref<HTMLElement | null>(null);

// PODZ-1699: width token is the only thing that varies across sizes — height
// and phone behavior stay consistent. Switch over a finite union so adding a
// new preset is a single-line change in this map.
const sizeClass = computed<string>(() => {
  switch (props.size) {
    case 'sm':
      return 'sm:max-w-[400px]';
    case 'xl':
      return 'sm:max-w-[1200px]';
    case 'lg':
      return 'sm:max-w-[720px]';
    case 'md':
    default:
      return 'sm:max-w-[480px]';
  }
});

function close() {
  // D-09 / AC#13: a non-closable modal is non-dismissible. The pay modal
  // flips `closable=false` during an in-flight charge.
  if (!props.closable) return;
  emit('update:visible', false);
}

function onKeyDown(e: KeyboardEvent) {
  // WR-04: gate on props.visible so hidden modal instances do not consume
  // Escape. Multiple MidFlowModal instances can be mounted simultaneously
  // (e.g. the schedule wizard's discard / phone / address / invalid-address
  // modals); without this gate, a single Escape press would close every
  // instance that registered a listener, not just the visible one.
  if (!props.visible) return;
  // D-09 / AC#13: Escape is also gated on `closable` (close() short-circuits,
  // but bail early so a non-closable modal never even evaluates the key).
  if (!props.closable) return;
  if (e.code === 'Escape' && !e.isComposing) close();
}

// Phase 5.3 Pitfall 1 (HIGHEST-RISK BUG): iframe-aware focus trap.
//
// A naive focus trap listens for focus leaving the dialog root and yanks it
// back. But a cross-origin gateway iframe (GPI/Stax hosted card field) is a
// FOCUSABLE DESCENDANT of the trapped root, and when the user clicks/tabs into
// it the browser reports the trap-root as having lost focus (the active element
// becomes the <iframe> element and focus crosses the origin boundary). A naive
// trap reads that as "focus escaped" and pulls focus back out of the card
// field — the customer literally cannot type their card number (confirmed
// across React Spectrum #1168, MUI #17497, Angular CDK #24059, Reach #615).
//
// The fix: only redirect focus back into the panel when the new focus target
// is NEITHER inside the panel NOR an <iframe> descendant of the panel. We do
// NOT Teleport the iframe outside the trapped root; the iframe stays a child of
// the panel and is allowed to hold focus. We also tab-cycle within the panel's
// own focusable controls (Tab / Shift+Tab) without disturbing iframe focus.
function isInsidePanel(node: Node | null): boolean {
  return !!node && !!panelRef.value && panelRef.value.contains(node);
}

// Only the topmost open modal owns the focus trap. A nested modal (pushed last)
// is topmost; the modal(s) beneath it yield so their document `focusin`/Tab
// traps do not fight the nested modal's trap (which previously recursed into a
// stack overflow). A lone modal is always topmost, so single-modal behavior is
// unchanged.
function isTopmostModal(): boolean {
  return (
    openModalPanels.length > 0 &&
    openModalPanels[openModalPanels.length - 1] === panelRef.value
  );
}

function removeFromModalStack(): void {
  const el = panelRef.value;
  if (!el) return;
  const i = openModalPanels.indexOf(el);
  if (i !== -1) openModalPanels.splice(i, 1);
}

function getFocusable(): HTMLElement[] {
  if (!panelRef.value) return [];
  return Array.from(
    panelRef.value.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea, input, select, iframe, [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((el) => el.offsetParent !== null || el.tagName === 'IFRAME');
}

function onFocusIn(e: FocusEvent) {
  if (!props.visible || !panelRef.value) return;
  // Yield unless this is the topmost open modal — prevents the nested-modal
  // focusin recursion (RangeError) when a second MidFlowModal stacks on top.
  if (!isTopmostModal()) return;
  const target = e.target as Node | null;
  // Focus landed inside the panel (including into a gateway <iframe>): allow it.
  if (isInsidePanel(target)) return;
  // Focus escaped the panel entirely — pull it back to the first focusable.
  const focusable = getFocusable();
  (focusable[0] ?? panelRef.value).focus();
}

function onTrapKeyDown(e: KeyboardEvent) {
  if (!props.visible || e.key !== 'Tab' || !panelRef.value) return;
  // Only the topmost modal intercepts Tab cycling (mirrors onFocusIn).
  if (!isTopmostModal()) return;
  const active = document.activeElement;
  // If focus currently sits inside a gateway <iframe>, the browser owns Tab
  // routing within that cross-origin field — do NOT intercept (Pitfall 1).
  if (active && active.tagName === 'IFRAME' && isInsidePanel(active)) {
    return;
  }
  const focusable = getFocusable();
  if (focusable.length === 0) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (e.shiftKey && active === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && active === last) {
    e.preventDefault();
    first.focus();
  }
}

watch(
  () => props.visible,
  async (isVisible) => {
    if (isVisible) {
      await nextTick();
      // Push onto the shared stack BEFORE focusing so this modal is already
      // topmost when its initial .focus() dispatches `focusin` (so the modal
      // beneath it correctly yields).
      if (panelRef.value && !openModalPanels.includes(panelRef.value)) {
        openModalPanels.push(panelRef.value);
      }
      const focusable = getFocusable();
      (focusable[0] ?? panelRef.value)?.focus();
    } else {
      removeFromModalStack();
    }
  },
  { immediate: true },
);

onMounted(() => {
  window.addEventListener('keydown', onKeyDown);
  document.addEventListener('focusin', onFocusIn);
  document.addEventListener('keydown', onTrapKeyDown);
});
onUnmounted(() => {
  window.removeEventListener('keydown', onKeyDown);
  document.removeEventListener('focusin', onFocusIn);
  document.removeEventListener('keydown', onTrapKeyDown);
  // Guard against a modal unmounting while still visible (leaves no stale panel
  // on the shared stack, which would make a sibling modal think it isn't top).
  removeFromModalStack();
});
</script>

<template>
  <Teleport to="body">
    <Transition name="mid-flow-modal">
      <div
        v-if="props.visible"
        v-bind="$attrs"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        @click.self="close"
      >
        <!-- PODZ-1698: `sm:max-h-[calc(100dvh-2rem)]` caps the desktop panel
             to viewport so a tall body (e.g. Reschedule's Calendar + TimeSlots)
             doesn't push the modal past the fold. Combined with the
             content-div `flex-1 overflow-y-auto min-h-0` below, the header
             and footer stay anchored and only the body scrolls. On phones
             (`max-sm:h-dvh`) the same flex column rule applies, giving the
             modal an internal scroll instead of bleeding off the viewport. -->
        <div
          ref="panelRef"
          role="dialog"
          :aria-label="props.title"
          aria-modal="true"
          tabindex="-1"
          :class="[
            'bg-surface w-full flex flex-col',
            'max-sm:h-dvh max-sm:w-screen max-sm:mx-0 max-sm:rounded-none max-sm:rounded-t-none',
            'sm:rounded-lg sm:shadow-lg sm:mx-4 sm:max-h-[calc(100dvh-2rem)]',
            sizeClass,
          ]"
        >
          <!-- Header (omitted for compact confirm dialogs via hideHeader) -->
          <div
            v-if="!hideHeader"
            class="px-6 py-4 border-b border-border flex items-center justify-between"
          >
            <span class="text-xl font-semibold text-foreground">{{
              props.title
            }}</span>
            <!-- D-09 / AC#13: the close button is gated on `closable`. While a
                 payment is in flight the pay modal sets `closable=false` and
                 the button is removed (not merely styled disabled), so it can
                 neither be clicked nor reached via the focus trap. -->
            <button
              v-if="props.closable"
              type="button"
              class="inline-flex items-center justify-center size-8 rounded-md text-muted hover:text-foreground hover:bg-canvas transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-0"
              :aria-label="'Close'"
              @click="close"
            >
              <svg
                class="size-4"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 1L13 13M1 13L13 1"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
              </svg>
            </button>
          </div>
          <!-- Content: PODZ-1698 — flex-1 + overflow-y-auto + min-h-0 so the
               body scrolls inside the modal instead of being clipped past the
               viewport (matters most on phone `max-sm:h-dvh`, where tall
               bodies like Reschedule's Calendar + TimeSlots otherwise hide
               below the fold with no scroll affordance). `min-h-0` is the
               flex-child unlock that lets overflow-y-auto actually scroll. -->
          <div class="px-6 py-4 flex-1 min-h-0 overflow-y-auto">
            <slot />
          </div>
          <!-- Footer -->
          <div
            v-if="$slots.footer"
            class="px-6 py-4 border-t border-border flex justify-end gap-3"
          >
            <slot name="footer" />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.mid-flow-modal-enter-active,
.mid-flow-modal-leave-active {
  transition:
    transform 0.22s ease,
    opacity 0.22s ease;
}
.mid-flow-modal-enter-from,
.mid-flow-modal-leave-to {
  transform: translateY(24px);
  opacity: 0;
}
@media (prefers-reduced-motion: reduce) {
  .mid-flow-modal-enter-active,
  .mid-flow-modal-leave-active {
    transition: opacity 0.22s ease;
  }
  .mid-flow-modal-enter-from,
  .mid-flow-modal-leave-to {
    transform: none;
  }
}
</style>
